import React, { useRef } from 'react';
import * as THREE from 'three';
import { useFrame } from '@react-three/fiber';

interface GlassTubeProps {
  position?: [number, number, number];
  rotation?: [number, number, number];
  scale?: number;
  /** Length of the tube in metres. Default 0.12 (12 cm). */
  length?: number;
  /** Inner-radius of the tube wall. Default 0.012. */
  radius?: number;
  /** Orientation: 'horizontal' lays the tube on its X-axis; 'vertical' on Y. */
  orientation?: 'horizontal' | 'vertical';
  /** Temperature in °C — controls the orange emissive glow in the middle. */
  temperatureC?: number;
  /** Whether to fade in/out gracefully. */
  visible?: boolean;
  children?: React.ReactNode;
}

/**
 * Generic borosilicate glass tube — used as the 12 cm tube containing
 * the CuO sample (horizontal) or the 10 cm gas exit tube on the gas
 * generator (vertical). Material is a thin transparent shell. When
 * `temperatureC` exceeds ~250 °C, the middle of the tube glows faintly
 * orange to communicate heating.
 *
 * Children passed in render INSIDE the tube's interior frame, so the
 * CuOPowder pile can sit at the middle of a horizontal tube.
 */
export function GlassTube({
  position = [0, 0, 0],
  rotation = [0, 0, 0],
  scale = 1,
  length = 0.12,
  radius = 0.012,
  orientation = 'horizontal',
  temperatureC = 25,
  visible = true,
  children,
}: GlassTubeProps) {
  const glowRef = useRef<THREE.MeshStandardMaterial>(null);
  const groupRef = useRef<THREE.Group>(null);
  const scaleRef = useRef(visible ? 1 : 0);

  useFrame((s) => {
    if (groupRef.current) {
      scaleRef.current += ((visible ? 1 : 0) - scaleRef.current) * 0.12;
      groupRef.current.scale.setScalar(scaleRef.current);
    }
    if (glowRef.current) {
      const t = Math.max(0, Math.min(1, (temperatureC - 250) / 600));
      const flicker = t > 0 ? Math.sin(s.clock.elapsedTime * 4) * 0.08 : 0;
      glowRef.current.emissiveIntensity = t * (0.7 + flicker);
      const r = 0.85 + 0.1 * t;
      const g = 0.30 + 0.40 * t;
      const b = 0.05 + 0.10 * t;
      glowRef.current.emissive.setRGB(r, g, b);
    }
  });

  const isHorizontal = orientation === 'horizontal';
  // The cylinder geometry is created along its local Y axis; we rotate
  // the whole tube into the X axis for horizontal orientation.
  const tubeRotation: [number, number, number] = isHorizontal ? [0, 0, Math.PI / 2] : [0, 0, 0];

  return (
    <group ref={groupRef} position={position} rotation={rotation} scale={scale}>
      <group rotation={tubeRotation}>
        {/* Glass wall */}
        <mesh castShadow>
          <cylinderGeometry args={[radius, radius, length, 28, 1, true]} />
          <meshPhysicalMaterial
            color="#eef6ff"
            transparent
            opacity={0.35}
            transmission={0.92}
            roughness={0.05}
            metalness={0.05}
            ior={1.50}
            thickness={0.004}
            side={THREE.DoubleSide}
          />
        </mesh>
        {/* Inner heating glow — overlapping shell that emissive ramps with temperature */}
        <mesh>
          <cylinderGeometry args={[radius * 0.92, radius * 0.92, length * 0.35, 24, 1, true]} />
          <meshStandardMaterial
            ref={glowRef}
            color="#221b14"
            transparent
            opacity={0.0}
            emissive="#000000"
            emissiveIntensity={0}
            side={THREE.DoubleSide}
            depthWrite={false}
          />
        </mesh>
        {/* End rims */}
        <mesh position={[0, length / 2, 0]} rotation={[Math.PI / 2, 0, 0]}>
          <torusGeometry args={[radius * 1.04, 0.0018, 8, 24]} />
          <meshStandardMaterial color="#cad6e2" roughness={0.45} metalness={0.2} />
        </mesh>
        <mesh position={[0, -length / 2, 0]} rotation={[Math.PI / 2, 0, 0]}>
          <torusGeometry args={[radius * 1.04, 0.0018, 8, 24]} />
          <meshStandardMaterial color="#cad6e2" roughness={0.45} metalness={0.2} />
        </mesh>
      </group>
      {/* Interior content slot (children render at the tube's midpoint) */}
      {children}
    </group>
  );
}

export default GlassTube;
